import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  api,
  apiJson,
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
} from './api.js';
import { trackEvent } from './analytics.js';

const AuthCtx = createContext({
  user: null,
  loading: false,
  configured: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  refresh: async () => {},
  signInWithEmail: async () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(Boolean(getAccessToken()));

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const res = await api('/api/users/me');
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
        return data.user;
      }
      clearTokens();
      setUser(null);
      return null;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async ({ email, password, captchaToken }) => {
    const data = await apiJson('/api/auth/login', {
      method: 'POST',
      body: { email, password, captchaToken },
    });
    setTokens(data);
    setUser(data.user);
    trackEvent('login', { method: 'password', role: data.user?.role });
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const data = await apiJson('/api/auth/register', {
      method: 'POST',
      body: payload,
    });
    setTokens(data);
    setUser(data.user);
    trackEvent('sign_up', {
      method: 'password',
      role: data.user?.role,
      marketing_consent: Boolean(payload.marketingConsent),
    });
    return { user: data.user, emailVerificationToken: data.emailVerificationToken };
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    try {
      if (refreshToken) {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch {
      /* ignore */
    }
    trackEvent('logout');
    clearTokens();
    setUser(null);
  }, []);

  const value = {
    user,
    loading,
    configured: true,
    login,
    register,
    logout,
    refresh,
    // Back-compat aliases used by some legacy components
    signInWithEmail: async () => {
      throw new Error('Magic-link sign in is deprecated. Use email + password.');
    },
    signOut: logout,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
